/**
 * Scoreboard abstraction.
 *
 * The MVP stores personal bests in localStorage, but nothing outside this module
 * knows that: swap in an `HttpScoreRepository` implementing the same async
 * interface and the rest of the game does not change. The async signatures exist
 * purely so that later swap is a drop-in.
 *
 * Bests are bucketed by *category*: a run at 0.00 ‰ and a run at 3.50 ‰ are not
 * the same game, so they do not share a leaderboard. See `categoryKey()`.
 */

import type { IntoxicationLevel } from '../config/intoxication';
import type { Split } from '../world/Checkpoint';

export interface RunResult {
  /** Absent only in historical local saves, which keep their old category. */
  seed?: number;
  fingerprint?: string;
  /** Final run time in milliseconds, derived from the fixed-step simulation. */
  timeMs: number;
  splits: Split[];
  playerName: string;
  levelName: string;
  /** The dose this run was set at - part of the run's identity. */
  intoxication: IntoxicationLevel;
  /** Bucket this run competes in. Includes dose, seed and simulation fingerprint. */
  category: string;
  /** Number of times Hannu landed a swipe. Metadata / bragging rights. */
  hannuHits: number;
  /** Wall-clock timestamp - metadata only, never used by gameplay. */
  recordedAt: number;
  /**
   * The server-issued run id this attempt was played under, when the game was
   * online. Absent for an offline run: still a perfectly good personal best,
   * just not something the leaderboard can accept.
   */
  runId?: string;
  /**
   * How many input frames were recorded. A result with no replay behind it can
   * never be submitted, because there would be nothing for the server to check.
   */
  replayFrames?: number;
}

export interface PersonalBestComparison {
  isPersonalBest: boolean;
  /** Negative = faster than the previous PB. Null when there was no PB. */
  deltaMs: number | null;
  previousBestMs: number | null;
}

export interface ScoreRepository {
  loadPersonalBest(category: string): Promise<RunResult | null>;
  savePersonalBest(run: RunResult): Promise<void>;
  /** All stored bests, newest categories first. Powers a future scoreboard. */
  loadAll(): Promise<RunResult[]>;
  clear(): Promise<void>;
}

/** Pure comparison logic - deliberately separate from any storage concern. */
export function comparePersonalBest(
  previous: RunResult | null,
  candidate: RunResult,
): PersonalBestComparison {
  if (!previous) {
    return { isPersonalBest: true, deltaMs: null, previousBestMs: null };
  }
  return {
    isPersonalBest: candidate.timeMs < previous.timeMs,
    deltaMs: candidate.timeMs - previous.timeMs,
    previousBestMs: previous.timeMs,
  };
}
